import { ChangeEvent, useMemo, useState } from 'react'

import { Flex, SearchFilterList, SearchInput, Switch } from '@percent/lemonade'

import { useOrganisationProfileContext } from '../../context/OrganisationProfileContext'
import { useTranslation } from '../../hooks/useTranslation'

export type OrganisationFiltersValue = {
  query: string
  countryCode?: string
  // Partner-only filter; leave undefined to hide the switch.
  engaged?: boolean
}

export type OrganisationFiltersProps = {
  value: OrganisationFiltersValue
  onChange: (value: OrganisationFiltersValue) => void
}

export function OrganisationFilters({
  value,
  onChange,
}: Readonly<OrganisationFiltersProps>) {
  const { t } = useTranslation()
  const { countries, locale } = useOrganisationProfileContext()
  const [countrySearch, setCountrySearch] = useState('')

  const countryOptions = useMemo(
    () =>
      countries
        .map(({ code, name }) => ({ value: code, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label, locale)),
    [countries, locale]
  )
  const filteredCountryOptions = countryOptions.filter((option) =>
    option.label.toLowerCase().includes(countrySearch.toLowerCase())
  )

  return (
    <Flex align="center" gap={12} w="100%">
      <Flex w="100%" maw="480">
        <SearchInput
          name="organisation-search"
          value={value.query}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, query: event.target.value })
          }
          handleClearValue={() => onChange({ ...value, query: '' })}
          placeholder={t('organisations.search.placeholder')}
          inputSize="small"
        />
      </Flex>
      <SearchFilterList
        label={t('organisations.search.country')}
        options={filteredCountryOptions}
        selectedValues={value.countryCode ? [value.countryCode] : []}
        onChange={(countryCodes: string[]) =>
          onChange({
            ...value,
            countryCode: countryCodes.find(
              (code) => code !== value.countryCode
            ),
          })
        }
        searchValue={countrySearch}
        onSearchChange={(event) => setCountrySearch(event.target.value)}
        handleClearValue={() => setCountrySearch('')}
        onKeyDown={() => undefined}
        placeholder={t('organisations.search.countrySearchPlaceholder')}
        noResultsFoundText={t('organisations.search.noCountriesFound')}
        testId="organisation-country-filter"
      />
      {value.engaged !== undefined && (
        <Switch
          label={t('organisations.search.engaged')}
          checked={value.engaged}
          onChange={(event) =>
            onChange({ ...value, engaged: event.currentTarget.checked })
          }
        />
      )}
    </Flex>
  )
}
